<template>
  <EditDialog
    :visible="visible"
    :title="$t('organization_structure.edit_site')"
    @close="$emit('close')"
  >
    <template v-slot:content>
      <v-form v-if="form.site._id" v-model="form.valid" ref="edit_site_form">
        <v-text-field
          required
          outlined
          dense
          data-private
          autofocus
          v-model="form.site.name"
          :maxLength="$constants.site.nameMaxLength"
          :hide-details="Boolean(form.site.name)"
          :class="Boolean(form.site.name) ? 'mb-4' : ''"
          :rules="[
            name => !!name || $t('validation.form.this_field_is_required'),
          ]"
          :label="`${$t('site_settings.site_name')}*`"
          :disabled="loading"
        />
        <v-select
          outlined
          required
          dense
          item-text="name"
          v-model="form.site.language"
          :hide-details="Boolean(form.site.language)"
          :class="Boolean(form.site.language) ? 'mb-4' : ''"
          :rules="[
            language =>
              !!language || $t('validation.form.this_field_is_required'),
          ]"
          :items="availableLanguages"
          :label="`${$t('organization.default_language')}*`"
          :disabled="loading"
        />
        <v-select
          outlined
          required
          dense
          v-model="form.site.currency"
          data-testid="settings-textField-currency"
          :hide-details="Boolean(form.site.currency)"
          :class="Boolean(form.site.currency) ? 'mb-4' : ''"
          :rules="[
            currency =>
              !!currency || $t('validation.form.this_field_is_required'),
          ]"
          :label="`${$t('common.currency')}*`"
          :items="currencies"
          :disabled="loading"
        />
        <v-autocomplete
          v-model="form.site.timezone"
          data-testid="settings-textField-timezone"
          dense
          outlined
          required
          :hide-details="Boolean(form.site.timezone)"
          :class="Boolean(form.site.timezone) ? 'mb-4' : ''"
          :rules="[
            timezone =>
              !!timezone || $t('validation.form.this_field_is_required'),
          ]"
          :label="`${$t('site_settings.timezone')}*`"
          :items="timezoneOptions"
          :disabled="loading"
        />
        <div class="d-flex">
          <label
            class="blue-grey--text text--darken-3 ma-0 pa-0 me-2 body-1"
            for="enable-site"
            >{{ $t('common.enabled') }}</label
          >
          <v-switch
            id="enable-site"
            class="mt-0 pt-0"
            hide-details
            v-model="form.site.isEnabled"
            :ripple="false"
            :disabled="loading"
          />
        </div>
      </v-form>
    </template>
    <template v-slot:footer>
      <div>
        <v-btn
          rounded
          text
          color="blue-grey"
          class="me-4"
          :disabled="loading"
          @click="$emit('close')"
          >{{ $t('common.button.cancel') }}</v-btn
        >
        <v-btn
          rounded
          color="blue"
          class="white--text"
          elevation="0"
          :disabled="loading"
          :loading="loading"
          @click="handleSave"
          >{{ $t('common.button.save') }}</v-btn
        >
      </div>
    </template>
  </EditDialog>
</template>

<script>
import EditDialog from '@/containers/OrganizationStructureView/Shared/EditDialog.vue'
import globalCurrencies from '@/locales/currencies.json'

import { utils } from 'frontend-js-shared'
import mixin from '@/mixins/mixin.vue'

export default {
  name: 'EditSiteDialog',

  mixins: [mixin],

  components: {
    EditDialog,
  },

  props: {
    loading: Boolean,
    visible: Boolean,
    site: {
      type: Object,
      required: false,
      default: () => {
        return { name: '', id: '' }
      },
    },
    organization: Object,
  },

  data() {
    return {
      availableLanguages: this.getLanguages(),
      form: {
        valid: true,
        site: {
          name: '',
          id: '',
        },
      },
      timezoneOptions: [],
      globalCurrencies: Object.values(globalCurrencies).map(
        ({ name, code }) => ({
          name,
          value: code,
          text: `${code} - ${name}`,
        })
      ),
    }
  },

  watch: {
    site(newVal) {
      this.form.site = { ...newVal }
    },
  },

  async mounted() {
    this.timezoneOptions = await utils.dates.humanizeTimezones()
  },

  computed: {
    currencies() {
      return this.globalCurrencies?.filter(this.isInOrganizationCurrency)
    },
  },

  methods: {
    isInOrganizationCurrency(currency) {
      const values = this.organization?.allowedSiteCurrencies?.map(
        c => c.currency
      )
      return values?.includes(currency.value)
    },

    handleSave() {
      this.$refs.edit_site_form.validate()
      if (!this.form.valid) return
      this.$emit('save', this.form.site)
      this.resetForm()
    },

    resetForm() {
      this.form.site = {
        name: '',
        id: '',
      }
      this.$refs?.edit_site_form?.reset?.()
    },
  },
}
</script>
